/**
 * Zod schemas for the seller self-registration wizard (STORY-006-010).
 *
 * Error messages are i18n KEYS, resolved in the `seller-registration`
 * namespace — the same pattern used in `features/auth/schemas.ts`.
 *
 * Backend constraints mirrored verbatim:
 *   business_name   2–120 chars
 *   market_id       UUID (selected from dropdown)
 *   pavilion_number 1–20 chars
 *   entrance_hint   0–500 chars
 *   phone_e164      /^\+[1-9]\d{7,14}$/ (matches the backend _E164_RE)
 *   whatsapp_phone  same E.164 pattern, optional
 *   telegram_handle /^@?[a-zA-Z0-9_]{5,32}$/ optional
 */
import { z } from "zod";

// ── Primitive patterns ──────────────────────────────────────────────────────

/**
 * Seller-registration E.164: leading non-zero country digit, then 7–14 more
 * digits. Mirrors the backend `_E164_RE` so a bad number fails inline.
 */
const PHONE_E164_RE = /^\+[1-9]\d{7,14}$/;

/** Telegram handle: optional leading @, 5–32 alphanumeric/underscore. */
const TELEGRAM_RE = /^@?[a-zA-Z0-9_]{5,32}$/;

// ── Start step ─────────────────────────────────────────────────────────────

export const startSchema = z.object({
  email: z
    .string({ required_error: "errors.required" })
    .min(1, { message: "errors.required" })
    .email({ message: "errors.emailInvalid" }),
});
export type StartFormValues = z.infer<typeof startSchema>;
export const startDefaultValues: StartFormValues = { email: "" };

// ── Profile step ───────────────────────────────────────────────────────────

export const profileSchema = z.object({
  business_name: z
    .string({ required_error: "errors.required" })
    .min(2, { message: "errors.businessNameMin" })
    .max(120, { message: "errors.businessNameMax" }),
  market_id: z
    .string({ required_error: "errors.marketRequired" })
    .uuid({ message: "errors.marketRequired" }),
  pavilion_number: z
    .string({ required_error: "errors.required" })
    .min(1, { message: "errors.required" })
    .max(20, { message: "errors.pavilionMax" }),
  entrance_hint: z
    .string()
    .max(500, { message: "errors.entranceMax" })
    .optional(),
  phone_e164: z
    .string({ required_error: "errors.required" })
    .regex(PHONE_E164_RE, { message: "errors.phoneFormat" }),
  whatsapp_phone_e164: z
    .string()
    .regex(PHONE_E164_RE, { message: "errors.phoneFormat" })
    .optional()
    .or(z.literal("")),
  telegram_handle: z
    .string()
    .regex(TELEGRAM_RE, { message: "errors.telegramFormat" })
    .optional()
    .or(z.literal("")),
});
export type ProfileFormValues = z.infer<typeof profileSchema>;
export const profileDefaultValues: ProfileFormValues = {
  business_name: "",
  market_id: "",
  pavilion_number: "",
  entrance_hint: "",
  phone_e164: "",
  whatsapp_phone_e164: "",
  telegram_handle: "",
};

// ── OTP step (shared for both email + phone) ───────────────────────────────

export const otpSchema = z.object({
  otp: z
    .string({ required_error: "errors.required" })
    .regex(/^\d{6}$/, { message: "errors.required" }),
});
export type OtpFormValues = z.infer<typeof otpSchema>;
export const otpDefaultValues: OtpFormValues = { otp: "" };

// ── Wizard steps ───────────────────────────────────────────────────────────

export const WIZARD_STEPS = [
  "profile",
  "email-otp",
  "phone-otp",
  "review",
] as const;
export type WizardStep = (typeof WIZARD_STEPS)[number];

/** Step index for StepDots (1-based). Start is not a dot — wizard shows 4 dots. */
export function stepIndex(step: WizardStep): number {
  return WIZARD_STEPS.indexOf(step) + 1;
}

// ── localStorage key ───────────────────────────────────────────────────────

export const SELLER_REG_STORAGE_KEY = "seller_registration.application_id";

/** Read the saved application_id UUID from localStorage. Returns null if absent/invalid. */
export function loadSavedApplicationId(): string | null {
  try {
    const raw = localStorage.getItem(SELLER_REG_STORAGE_KEY);
    if (!raw) return null;
    // Validate it looks like a UUID (basic check — not PII).
    if (/^[0-9a-f-]{36}$/i.test(raw)) return raw;
    return null;
  } catch {
    return null;
  }
}

/** Save the application_id UUID to localStorage. */
export function saveApplicationId(id: string): void {
  try {
    localStorage.setItem(SELLER_REG_STORAGE_KEY, id);
  } catch {
    /* storage unavailable — non-fatal */
  }
}

/** Clear the saved application_id from localStorage. */
export function clearApplicationId(): void {
  try {
    localStorage.removeItem(SELLER_REG_STORAGE_KEY);
  } catch {
    /* storage unavailable — non-fatal */
  }
}
